// PovifyList.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faComment, faCommentDots, faPlus } from '@fortawesome/free-solid-svg-icons';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';

const PovifyList = () => {
  const authState = useSelector((state) => state.authReducer);
  const userId = authState.user?._id;
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const [povifyData, setPovifyData] = useState([]);
  const [povifyCommentData, setPovifyCommentData] = useState([]);
  const [selectedPovifyId, setSelectedPovifyId] = useState(null);
  const [viewingComments, setViewingComments] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const [userComment, setUserComment] = useState('');
  const [povifyId, setPovifyId] = useState('');
  const [povifyTitle, setPovifyTitle] = useState('');
  const [postedCommentMsg, setPostedCommentMsg] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch POVify data
  const fetchPovify = useCallback(() => {
    if (!authState.isLoggedIn || !userId) return;
    const config = {
      url: '/povify/my',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { userId },
    };
    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response?.status) {
          setPovifyData(response.povifyList || []);
        }
      })
      .catch((error) => console.error('Error fetching POVify data:', error));
  }, [authState.isLoggedIn, authState.token, fetchData, userId]);

  useEffect(() => {
    fetchPovify();
  }, [fetchPovify]);

  // Fetch comments for a specific POVify
  const fetchPovifyComments = useCallback((povifyId) => {
    if (!authState.isLoggedIn || !povifyId) return;
    const config = {
      url: '/povifycomments/povifyid',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { povifId: povifyId },
    };
    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response?.status) {
          setPovifyCommentData(response.povifyComments || []);
        }
      })
      .catch((error) => console.error('Error fetching POVify comments:', error));
  }, [authState.isLoggedIn, authState.token, fetchData]);

  // Post comment
  const postMyComment = useCallback((povifyId, povifyTitle) => {
    if (!userComment.trim()) {
      setErrors({ comment: 'Comment must not be blank' });
      return;
    }
    setErrors({});
    setPostedCommentMsg('');
    const config = {
      url: '/povifycomments',
      method: 'post',
      headers: { Authorization: authState.token },
      data: {
        povifId: povifyId,
        userComment,
        userId,
        povifyTitle,
        dateCommented: new Date(),
      },
    };
    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response?.status) {
          setPostedCommentMsg('Comment posted successfully!');
          setUserComment('');
          setShowPostForm(false);
          fetchPovifyComments(povifyId); // Refetch comments
        } else {
          setPostedCommentMsg('Error posting comment. Please try again.');
        }
      })
      .catch((error) => {
        console.error('Error posting comment:', error);
        setPostedCommentMsg('Error posting comment. Please try again.');
      });
  }, [authState.token, fetchData, fetchPovifyComments, userComment, userId]);

  const sortedPovifyData = [...povifyData].sort((a, b) => new Date(b.dateStarted) - new Date(a.dateStarted));
  const sortedPovifyCommentData = [...povifyCommentData].sort((a, b) => new Date(b.dateCommented) - new Date(a.dateCommented));

  const handlePovifyView = (id) => {
    setSelectedPovifyId((prev) => (prev === id ? null : id));
    setViewingComments(false);
    setShowPostForm(false);
    setPovifyCommentData([]);
  };

  const handleToggleComments = (id) => {
    setViewingComments((prev) => !prev);
    setPovifyId(id);
    setPovifyTitle(''); // Reset or set from item
    if (!viewingComments) {
      fetchPovifyComments(id);
    }
  };

  const handleTogglePostForm = () => {
    setShowPostForm((prev) => !prev);
    if (!showPostForm) {
      setUserComment('');
      setErrors({});
      setPostedCommentMsg('');
    }
  };

  const handlePostComment = () => {
    postMyComment(povifyId, povifyTitle);
  };

  const handleCreatePovify = () => {
    navigate('/povify-create');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  if (!authState.isLoggedIn) {
    return <Loader />;
  }

  return (
    <div>
      <div className="min-h-screen bg-black text-white px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-6 text-center bg-black border border-teal-500">
          <h1 className="text-xl lg:text-2xl font-bold text-blue-300 sm:text-3xl">POVify</h1>
        </header>

        {/* Create Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={handleCreatePovify}
            className="flex items-center justify-center w-40 px-4 py-2 bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg text-white font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
            aria-label="Create new POV"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Create POV
          </button>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <Loader />
          ) : povifyData.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-yellow-400">
              <span className="text-lg">No records found! 📝</span>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedPovifyData.map((item, index) => (
                <div
                  key={item._id}
                  className="bg-gray-800 border border-gray-700 rounded-lg shadow-md overflow-hidden"
                >
                  {/* Summary */}
                  <div className="flex items-center justify-between p-4 border border-teal-500 cursor-pointer hover:bg-gray-750 transition-colors">
                    <span className="font-semibold text-gray-100 flex-1">
                      {index + 1}. {item.povifyCode}: {item.povifyTitle}
                    </span>
                    <Tooltip text="View details" position="top">
                      <button
                        onClick={() => handlePovifyView(item._id)}
                        className="px-2 py-2 w-8 text-blue-400 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-m"
                        aria-label="View POV details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </Tooltip>
                  </div>

                  {/* Details - Expandable */}
                  {selectedPovifyId === item._id && (
                    <div className="p-4 space-y-4 bg-gray-750 border-t border-gray-600">
                      <div className="space-y-2 text-sm text-gray-300">
                        <p>
                          <span className="font-medium text-blue-300">POV Code:</span>{' '}
                          <strong>{item.povifyCode}</strong>
                        </p>
                        <p>
                          <span className="font-medium text-blue-300">Title:</span>{' '}
                          <strong>{item.povifyTitle}</strong>
                        </p>
                        <p>
                          <span className="font-medium text-blue-300">Brief:</span> {item.povifyBrief}
                        </p>
                        <p>
                          <span className="font-medium text-blue-300">Details:</span>{' '}
                          <span
                            className="max-w-none"
                            dangerouslySetInnerHTML={{ __html: item.povifyDetails }}
                          />
                        </p>
                        <p>
                          <span className="font-medium text-blue-300">Visibility:</span>{' '}
                          {item.povifyVisibility}
                        </p>
                        {item.povifyTeamCode && (
                          <p>
                            <span className="font-medium text-blue-300">Team:</span>{' '}
                            {item.povifyTeamName}
                          </p>
                        )}
                        {item.povifyRCALink && (
                          <p>
                            <span className="font-medium text-blue-300">RCA Code:</span>{' '}
                            {item.povifyRCALink}
                          </p>
                        )}
                        <p>
                          <span className="font-medium text-blue-300">Date:</span>{' '}
                          {formatDateTime(item.dateStarted)}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-2 pt-2">
                        <Tooltip text="Post a comment" position="top">
                          <button
                            onClick={handleTogglePostForm}
                            className="p-2 text-blue-400 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full transition-colors"
                            aria-label="Toggle post comment form"
                          >
                            <FontAwesomeIcon icon={faCommentDots} />
                          </button>
                        </Tooltip>
                        <Tooltip text="View comments" position="top">
                          <button
                            onClick={() => handleToggleComments(item._id)}
                            className="p-2 text-blue-400 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full transition-colors"
                            aria-label="Toggle comments"
                          >
                            <FontAwesomeIcon
                              icon={faComment}
                              className={viewingComments ? 'text-green-400' : ''}
                            />
                          </button>
                        </Tooltip>
                      </div>

                      {/* Post Comment Form */}
                      {showPostForm && (
                        <div className="space-y-3 p-4 bg-gray-700 rounded-lg border border-gray-600">
                          <textarea
                            value={userComment}
                            onChange={(e) => setUserComment(e.target.value)}
                            placeholder="Share your thoughts..."
                            className="w-full p-3 bg-gray-600 text-white border border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical min-h-[100px] placeholder-gray-400"
                            rows={4}
                          />
                          {errors.comment && (
                            <p className="text-red-400 text-sm">{errors.comment}</p>
                          )}
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={handleTogglePostForm}
                              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handlePostComment}
                              disabled={!userComment.trim()}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg text-white font-medium transition-colors"
                            >
                              Post Comment
                            </button>
                          </div>
                          {postedCommentMsg && (
                            <p
                              className={`text-sm ${
                                postedCommentMsg.includes('successfully') ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {postedCommentMsg}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Comments List */}
                      {viewingComments && (
                        <div className="space-y-3">
                          {sortedPovifyCommentData.length === 0 ? (
                            <p className="text-gray-400 text-center py-4">No comments yet. 💭</p>
                          ) : (
                            sortedPovifyCommentData.map((comment) => (
                              <div
                                key={comment._id}
                                className="p-4 bg-gray-700 rounded-lg border border-gray-600"
                              >
                                <div className="space-y-1 text-sm">
                                  <p>
                                    <span className="font-medium text-blue-300">By:</span>{' '}
                                    {comment.userName}
                                  </p>
                                  <p>
                                    <span className="font-medium text-blue-300">Comment:</span>{' '}
                                    {comment.userComment}
                                  </p>
                                  <p className="text-gray-400">
                                    <span className="font-medium">Date:</span>{' '}
                                    {formatDateTime(comment.dateCommented)}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PovifyList;